import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { jwtDecode } from 'jwt-decode';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import authService from '../../utils/login/AuthService';
import { useAuth } from '../../context/AuthContext';
import './Callback.css';

// 错误处理 API（如果需要，请根据实际 API 实现）
// const ViewCoreOperations = async (params) => {
//   return request({
//     url: '/api/error/log',
//     method: 'post',
//     data: params,
//   });
// };

// const CookieLogout = async () => {
//   return request({
//     url: '/api/auth/cookieLogout',
//     method: 'post',
//   });
// };

interface JwtToken {
  exp?: number;
  lid?: string;
  [key: string]: any;
}

const CallbackPage = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusText, setStatusText] = useState('正在读取授权信息...');
  const [subStatusText, setSubStatusText] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);

  useEffect(() => {
    // 防止重复处理：如果已经有 token，直接跳转
    const existingToken = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (existingToken) {
      const returnPath = '/'; // 跳转到首页
      navigate(returnPath, { replace: true });
      return;
    }

    const handleCallback = async () => {
      try {
        setStatusText('正在读取授权信息...');
        setSubStatusText('正在解析回调参数');
        
        // 调试信息：检查当前 URL 和存储
        console.log('回调页面 URL:', window.location.href);
        console.log('URL 参数:', new URLSearchParams(window.location.search).toString());
        
        setStatusText('正在验证身份...');
        setSubStatusText('正在向服务器发起登录请求');
        
        const user = await authService.handleCallback();
        
        if (!user) {
          setError('登录失败：未获取到用户信息');
          setStatusText('登录失败');
          setSubStatusText('未获取到用户信息');
          setLoading(false);
          return;
        }

        // 解析返回的用户信息数据 access_token
        if (!user.access_token) {
          setError('登录失败：未获取到访问令牌');
          setStatusText('登录失败');
          setSubStatusText('未获取到访问令牌');
          setLoading(false);
          return;
        }

        setStatusText('正在解析令牌...');
        setSubStatusText('正在验证用户信息');

        let jwtToken: JwtToken = {};
        try {
          jwtToken = jwtDecode<JwtToken>(user.access_token);
        } catch (decodeError) {
          console.error('JWT 解析失败:', decodeError);
          setError('登录失败：令牌解析错误');
          setStatusText('登录失败');
          setSubStatusText('令牌解析错误');
          setLoading(false);
          return;
        }

        setStatusText('正在保存登录信息...');
        setSubStatusText('正在初始化用户会话');

        // 存储用户名称 用于显示
        const realName = user.profile?.name || '';
        
        // tokenObj : 用于存储刷新token需要的信息
        const tokenObj = {
          expires_at: jwtToken.exp, // 过期时间
          refresh_token: user.refresh_token, // 刷新token
          loginIn: jwtToken.lid, //用户信息
        };

        // 存储完整的用户信息
        const userInfo = {
          id: jwtToken.lid || user.profile?.sub || '',
          name: realName,
          username: user.profile?.preferred_username || user.profile?.name || '',
          email: user.profile?.email || '',
          ...user.profile,
        };

        // 一次性设置所有存储，避免中间状态被 AuthGuard 检测到
        localStorage.setItem('access_token', user.access_token);
        localStorage.setItem('token', user.access_token);
        localStorage.setItem('realName', realName);
        localStorage.setItem('user_lid', jwtToken.lid || '');
        localStorage.setItem('tokenObj', JSON.stringify(tokenObj));
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        
        // 更新 AuthContext
        updateUser(userInfo);
        
        // 立即触发 tokenUpdated 事件，通知 AuthGuard
        window.dispatchEvent(new Event('tokenUpdated'));

        setStatusText('登录成功');
        setSubStatusText('正在跳转到系统...');
        setIsSuccess(true);
        setLoading(false);

        // 跳转到首页（使用简单的路径，避免路径不存在的问题）
        const state = user.state;
        const returnPath = state && state !== '/callback' ? state : '/';
        
        // 使用 setTimeout 确保 localStorage 和事件都已处理完成
        setTimeout(() => {
          navigate(returnPath, { 
            replace: true
          });
        }, 800);
      } catch (e: any) {
        console.error('Callback 处理错误:', e);
        // setError(e.message || '登录处理失败');
        // setStatusText('登录失败');
        // setSubStatusText(e.message || '登录处理失败，请稍后重试');
        setLoading(false);

        // 清除 sessionStorage

        // 检查是否是登录要求错误
        if (window.location.href.includes('error=login_require')) {
          setStatusText('需要重新登录');
          setSubStatusText('正在跳转到登录页面...');
          try {
            await authService.logout();
          } catch (logoutError) {
            console.error('登出失败:', logoutError);
          }
        } else {
          // 处理时间不一致错误
          const isTimeError = e.message && e.message.includes('exp is in the past');
          
          if (isTimeError) {
            setStatusText('时间不一致');
            setSubStatusText('您的电脑时间与服务器不一致');
            message.error({
              content: '您的电脑时间与服务器不一致，请重置系统时间，具体操作可前往飞书，搜索IT服务台，输入"登录时间报错"，获取更多操作攻略。',
              duration: 5,
            });
          }

          // 错误详情
          // const errorDetails = {
          //   message: e.message,
          //   stack: e.stack,
          //   name: e.name,
          // };

          // 事件异常处理（如果需要，请取消注释并实现 API）
          // const errorInfoForAPI = JSON.stringify(errorDetails);
          // const params = {
          //   name: 'sso回调页面', //事件名称
          //   category: 'callback', //事件类别
          //   eventType: 'Failure', //事件类型
          //   message: errorInfoForAPI, //事件信息
          //   url: `${window.location.origin}/callback`, //前端地址栏路由
          //   data: null, //事件详细信息
          // };
          // setTimeout(
          //   () => {
          //     ViewCoreOperations(params).finally(() => {
          //       CookieLogout().then(() => {
          //         window.location.href = window.location.origin;
          //       });
          //     });
          //   },
          //   isTimeError ? 5000 : 0,
          // );

          // // 如果没有实现错误处理 API，直接跳转到首页
          // if (!isTimeError) {
          //   setTimeout(() => {
          //     window.location.href = window.location.origin;
          //   }, 2000);
          //   alert('登录处理失败：' + e.message);
          // }
        }
      }
    };

    handleCallback();
  }, [navigate, updateUser]);

  return (
    <div className="callback-page">
      <div className="callback-card">
        {isSuccess ? (
          <CheckCircleOutlined className="callback-success-icon" />
        ) : error ? (
          <CloseCircleOutlined className="callback-error-icon" />
        ) : (
          <div className="callback-spinner"></div>
        )}
        <div className="callback-title">
          {isSuccess ? '登录成功' : error ? '登录失败' : '正在完成登录'}
        </div>
        <div className="callback-desc">
          {isSuccess 
            ? '请稍候，正在为您安全跳转...' 
            : error 
            ? '登录过程中出现问题，请查看下方提示'
            : '请稍候，正在为您安全跳转...'}
        </div>
        <div className={`callback-status ${error ? 'error' : ''}`}>
          {statusText}
        </div>
        {subStatusText && (
          <div className="callback-sub-status">{subStatusText}</div>
        )}
        {!isSuccess && !error && (
          <div className="callback-hint">
            如果长时间无响应，请关闭此页重新登录。
          </div>
        )}
        {error && (
          <div className="callback-hint" style={{ color: '#b91c1c' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallbackPage;